import { type FC, useState, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import {
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  TextInput,
  Breadcrumb,
  BreadcrumbItem,
} from "flowbite-react";
import { supabase, TABLES } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { HiSearch, HiHome } from "react-icons/hi";
import { MultiSelect } from "../../components/MultiSelect";
import DateRangePicker from "../../components/DateRangePicker";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  created_at: string;
  address: {
    street: string;
    house_number: string;
    zip_code: string;
    city: string;
  };
  contracts: {
    id: number;
    secure_token: string;
    contract_number: string;
    contract_type: {
      type: string;
      company: string;
    };
  }[];
}

const CustomerOverviewPage: FC = function () {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const getAddressText = (customer: Customer) =>
    [
      customer.address?.street,
      customer.address?.house_number,
      customer.address?.zip_code,
      customer.address?.city,
    ]
      .filter(Boolean)
      .join(" ");

  const getContractType = (contract: Customer["contracts"][number]) =>
    contract.contract_type?.type ?? "";

  const getContractCompany = (contract: Customer["contracts"][number]) =>
    contract.contract_type?.company ?? "";

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  // Extract unique values for filters
  const uniqueValues = useMemo(() => {
    const types = new Set<string>();
    const companies = new Set<string>();

    customers.forEach((customer) => {
      customer.contracts.forEach((contract) => {
        const type = getContractType(contract);
        const company = getContractCompany(contract);
        if (type) types.add(type);
        if (company) companies.add(company);
      });
    });

    return {
      types: Array.from(types).map((type) => ({
        value: type,
        label: type,
      })),
      companies: Array.from(companies).map((company) => ({
        value: company,
        label: company,
      })),
    };
  }, [customers]);

  // Filter customers based on search term and selected filters
  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      // Search term filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        searchTerm === "" ||
        `${customer.first_name} ${customer.last_name}`
          .toLowerCase()
          .includes(searchLower) ||
        getAddressText(customer).toLowerCase().includes(searchLower);

      // Contract type filters
      const matchesType =
        selectedTypes.length === 0 ||
        customer.contracts.some((contract) =>
          selectedTypes.includes(getContractType(contract))
        );

      const matchesCompany =
        selectedCompanies.length === 0 ||
        customer.contracts.some((contract) =>
          selectedCompanies.includes(getContractCompany(contract))
        );

      const matchesDateRange = () => {
        if (!dateRange.startDate || !dateRange.endDate) return true;
        const customerDate = new Date(customer.created_at);
        customerDate.setHours(0, 0, 0, 0);
        const start = new Date(dateRange.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999);
        return customerDate >= start && customerDate <= end;
      };

      return (
        matchesSearch && matchesType && matchesCompany && matchesDateRange()
      );
    });
  }, [customers, searchTerm, selectedTypes, selectedCompanies, dateRange]);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
      return;
    }

    const fetchCustomers = async () => {
      try {
        if (!user) return;

        Sentry.addBreadcrumb({
          category: "customer-overview.fetch",
          level: "info",
          message: "customer-overview.fetch.start",
          data: { route: "/customers/overview" },
        });

        const { data: worker, error: workerError } = await supabase
          .from(TABLES.WORKERS)
          .select("id")
          .eq("user_id", user.id)
          .single();

        if (workerError) throw workerError;

        // Start with contracts for this worker, then join with customers and other tables
        const { data, error } = await supabase
          .from(TABLES.CONTRACTS)
          .select(
            `
            id,
            secure_token,
            contract_number,
            contract_type:contract_type_id (
              type,
              company
            ),
            customer:customer_id (
              id,
              first_name,
              last_name,
              email,
              phone_number,
              created_at,
              address:address_id (
                street,
                house_number,
                zip_code,
                city
              )
            )
          `
          )
          .eq("worker_id", worker.id)
          .order("created_at", { ascending: false });

        if (error) throw error;

        // Transform and group by customer
        const customerMap = new Map<number, Customer>();

        (data || []).forEach((contract: any) => {
          if (!contract?.customer || !contract?.customer?.address) return;

          const customerId = contract.customer.id;

          if (!customerMap.has(customerId)) {
            // Create new customer entry
            customerMap.set(customerId, {
              id: contract.customer.id,
              first_name: contract.customer.first_name || "",
              last_name: contract.customer.last_name || "",
              email: contract.customer.email || "",
              phone_number: contract.customer.phone_number || "",
              created_at:
                contract.customer.created_at || new Date().toISOString(),
              address: {
                street: contract.customer.address.street || "",
                house_number: contract.customer.address.house_number || "",
                zip_code: contract.customer.address.zip_code || "",
                city: contract.customer.address.city || "",
              },
              contracts: [],
            });
          }

          // Add contract to customer's contracts array
          const customer = customerMap.get(customerId)!;
          customer.contracts.push({
            id: contract.id,
            secure_token: contract.secure_token || "",
            contract_number: contract.contract_number || "",
            contract_type: {
              type: contract.contract_type?.type || "",
              company: contract.contract_type?.company || "",
            },
          });
        });

        const transformedCustomers = Array.from(customerMap.values());
        Sentry.addBreadcrumb({
          category: "customer-overview.fetch",
          level: "info",
          message: "customer-overview.fetch.success",
          data: {
            route: "/customers/overview",
            customerCount: transformedCustomers.length,
          },
        });
        setCustomers(transformedCustomers);
      } catch (err) {
        console.error("Error fetching customers:", err);
        Sentry.captureException(err, {
          level: "warning",
          tags: {
            operation: "customer-overview.fetch",
            route: "/customers/overview",
            handled: "true",
          },
        });
        setError(
          err instanceof Error ? err.message : "Fehler beim Laden der Kunden"
        );
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchCustomers();
    }
  }, [user, authLoading, navigate]);

  if (loading || authLoading) {
    return (
      <NavbarSidebarLayout pageTitle="Customer Overview">
        <div className="flex h-full items-center justify-center">
          <Spinner size="xl" />
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (error) {
    return (
      <NavbarSidebarLayout pageTitle="Customer Overview">
        <div className="p-4">
          <div className="rounded-lg bg-red-50 p-4 text-red-800 dark:bg-gray-800 dark:text-red-400">
            <p>Fehler beim Laden der Kunden: {error}</p>
          </div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout pageTitle="Customer Overview">
      <div className="p-4">
        <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex lg:mt-1.5">
          <div className="mb-1 w-full">
            <div className="mb-4">
              <Breadcrumb className="mb-4">
                <BreadcrumbItem href="#">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">Startseite</span>
                  </div>
                </BreadcrumbItem>
                <BreadcrumbItem href="/customers">Kunden</BreadcrumbItem>
                <BreadcrumbItem>Übersicht</BreadcrumbItem>
              </Breadcrumb>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                Kundenübersicht
              </h1>
            </div>
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="w-full">
                <TextInput
                  icon={HiSearch}
                  placeholder="Nach Namen oder Adresse suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* Filter Dropdowns */}
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-full sm:w-auto">
                  <MultiSelect
                    id="contract-type-select"
                    label="Vertragstyp"
                    options={uniqueValues.types}
                    value={selectedTypes}
                    onChange={setSelectedTypes}
                    placeholder="Vertragstyp auswählen"
                    searchPlaceholder="Nach Vertragstyp suchen..."
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <MultiSelect
                    id="company-select"
                    label="Unternehmen"
                    options={uniqueValues.companies}
                    value={selectedCompanies}
                    onChange={setSelectedCompanies}
                    placeholder="Unternehmen auswählen"
                    searchPlaceholder="Nach Unternehmen suchen..."
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <DateRangePicker
                    label="Datum"
                    onChange={setDateRange}
                    className="w-full sm:w-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow">
                <Table
                  striped
                  className="dark:bg-gray-800 dark:text-gray-400"
                  theme={{
                    root: {
                      shadow: "hidden",
                      wrapper:
                        "relative overflow-x-auto shadow-md sm:rounded-lg",
                    },
                  }}
                >
                  <TableHead>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Kontakt</TableHeadCell>
                    <TableHeadCell>Adresse</TableHeadCell>
                    <TableHeadCell>Vertrag</TableHeadCell>
                    <TableHeadCell>Datum</TableHeadCell>
                    <TableHeadCell>Aktionen</TableHeadCell>
                  </TableHead>
                  <TableBody className="divide-y">
                    {filteredCustomers.map((customer) => (
                      <TableRow
                        key={customer.id}
                        className="bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      >
                        <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                          {customer.first_name} {customer.last_name}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-gray-900 dark:text-white">
                              {customer.email}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {customer.phone_number}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p>
                              {customer.address?.street ?? ""}{" "}
                              {customer.address?.house_number ?? ""}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {customer.address?.zip_code ?? ""}{" "}
                              {customer.address?.city ?? ""}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {customer.contracts.map((contract, index) => (
                            <div key={contract.contract_number}>
                              {index > 0 && (
                                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                              )}
                              <p className="text-gray-900 dark:text-white">
                                {getContractType(contract)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {getContractCompany(contract)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {contract.contract_number}
                              </p>
                            </div>
                          ))}
                        </TableCell>
                        <TableCell>
                          {new Date(customer.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-2">
                            {customer.contracts.map((contract) => (
                              <button
                                key={contract.id}
                                onClick={() =>
                                  navigate(
                                    `/customers/information/${contract.secure_token}`
                                  )
                                }
                                className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700"
                              >
                                Details anzeigen
                              </button>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default CustomerOverviewPage;
